import React from "react";
import {
  Cpu,
  Dices,
  Music,
  Clock,
  Star,
  Moon,
  Sparkle,
  Heart,
  ExternalLink,
  Trophy,
} from "lucide-react";

// Big whimsical doodles drifting behind the content.
const DOODLES = [
  { Icon: Dices, top: "14%", left: "8%", size: 72, opacity: 0.16, delay: "0s" },
  { Icon: Star, top: "24%", left: "84%", size: 48, opacity: 0.16, delay: "1.4s" },
  { Icon: Moon, top: "70%", left: "86%", size: 70, opacity: 0.13, delay: "0.7s" },
  { Icon: Music, top: "48%", left: "4%", size: 40, opacity: 0.13, delay: "1.8s" },
  { Icon: Sparkle, top: "82%", left: "12%", size: 44, opacity: 0.15, delay: "2.2s" },
];

// Gentle per-card tilts so the grid feels hand-arranged.
const TILTS = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1"];

const Interests = () => {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.22)] blur-3xl animate-aurora"
        style={{ animationDelay: "-7s" }}
      />
      {DOODLES.map(({ Icon, top, left, size, opacity, delay }, i) => (
        <Icon
          key={i}
          aria-hidden
          className="pointer-events-none absolute text-primary animate-drift"
          style={{ top, left, width: size, height: size, opacity, animationDelay: delay }}
        />
      ))}

      <div className="container relative mx-auto max-w-3xl px-4 py-20">
        <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          <Heart className="h-4 w-4" />
          things I love
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
          <span className="text-gold-shimmer">Interests</span>
        </h1>
        <p className="mt-5 max-w-xl text-lg text-muted-foreground">
          The things that keep me happily distracted — collected, queued for,
          and rambled about at length.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {/* Tech. */}
          <div
            className={`rounded-2xl border border-border bg-card/70 p-6 backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-primary/60 hover:glow-gold ${TILTS[0]}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary">
              <Cpu className="h-5 w-5" />
            </span>
            <h2 className="mt-4 font-semibold text-card-foreground">Tech</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Gadgets, code, and shiny new ideas. I love tinkering with
              whatever's next — this very site is one of my playgrounds.
            </p>
          </div>

          {/* Board games — collector, not designer. */}
          <a
            href="https://boardgamegeek.com/collection/user/TheDreamerKey"
            target="_blank"
            rel="noopener noreferrer"
            className={`group flex flex-col rounded-2xl border border-border bg-card/70 p-6 backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-primary/60 hover:glow-gold ${TILTS[1]}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-hover:animate-wiggle">
              <Dices className="h-5 w-5" />
            </span>
            <h2 className="mt-4 flex items-center gap-1.5 font-semibold text-card-foreground">
              Board Games
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-primary" />
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              I collect them — and even play them sometimes. Browse my shelves
              and ratings on BoardGameGeek.
            </p>
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
              <Trophy className="h-3.5 w-3.5" />
              Last played: Fit to Print
            </p>
          </a>

          {/* Live music. */}
          <div
            className={`rounded-2xl border border-border bg-card/70 p-6 backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-primary/60 hover:glow-gold ${TILTS[2]}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary">
              <Music className="h-5 w-5" />
            </span>
            <h2 className="mt-4 font-semibold text-card-foreground">
              Live Music
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              A crowd, a stage, and speakers you can feel in your ribs.
              Nothing beats hearing a favorite song played live.
            </p>
          </div>

          {/* Doctor Who — the reason for the blue-and-gold palette. */}
          <div
            className={`rounded-2xl border border-border bg-card/70 p-6 backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-[hsl(var(--blue)/0.6)] hover:shadow-[0_0_0_1px_hsl(var(--blue)/0.25),0_0_28px_-6px_hsl(var(--blue)/0.55)] ${TILTS[3]}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--blue)/0.15)] text-[hsl(var(--blue))]">
              <Clock className="h-5 w-5" />
            </span>
            <h2 className="mt-4 font-semibold text-card-foreground">
              Doctor Who
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              My favorite show in all of time and space — and yes, it's why
              this whole site is dressed in TARDIS blue and gold. Allons-y!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Interests;
